'use client';

import TextProcessor from "@/components/common/TextProcessor";
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Mousewheel, Pagination } from 'swiper/modules';
import { Fragment, useState, useRef, useEffect, useCallback } from "react";
import type { MouseEvent, TouchEvent } from "react"; // React event types
import { Dialog, DialogPanel, TransitionChild } from "@headlessui/react";
import Image from "next/image";
import type { Block } from "@/sections/SectionRenderer";
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { Swiper as SwiperType } from 'swiper';
import styles from './Team.module.css';
import { useWindowSize } from '@/hooks/useWindowSize';
import { useDarkMode } from "@/hooks/useDarkMode";
import { FaGithub } from 'react-icons/fa'; // ← add

interface Person {
  name: string;
  role: string;
  imageUrl: string;
  xUrl: string;
  linkedinUrl: string;
  githubUrl?: string; // ← add
  bio: string;
}

interface TeamData extends Block {
  people: Person[]
}

export function Team({ data }: { data: TeamData }) {
  const SLIDE_WIDTH = 400;
  const SLIDE_GAP = 35;
  const DESKTOP_BREAKPOINT = (SLIDE_WIDTH * 2) + SLIDE_GAP;
  
  const { isDesktop } = useWindowSize(DESKTOP_BREAKPOINT);
  const { isDarkMode, mounted } = useDarkMode();

  const { id, people } = data;

  const [isOpen, setIsOpen] = useState(false)
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null)
  const [displayedPerson, setDisplayedPerson] = useState<Person | null>(null)
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [popupState, setPopupState] = useState({ top: 0, left: 0, width: 400, height: 860 })

  // Update the ref type
  const swiperRef = useRef<SwiperType | null>(null);

  // Add these near the other state declarations
  const touchStart = useRef<number>(0);
  const touchEnd = useRef<number>(0);

  // Removed unused TRANSITION_DURATION constant
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);

  // Add a new state for image loading
  const [imageLoaded, setImageLoaded] = useState(false);

  // First add these state variables at the top of your component
  const [isAtStart, setIsAtStart] = useState(true); // Default to true since carousel starts at beginning
  const [isAtEnd, setIsAtEnd] = useState(false);

  function openModal(person: Person, event: MouseEvent<HTMLElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    const width = typeof window !== 'undefined' ? window.innerWidth : 0;
    const height = typeof window !== 'undefined' ? window.innerHeight : 0;
    const threshold = 1.3 // Prevents element ending up too close to edges even if not overlapping.

    const popup = {
      width: width / 2.5, // dialog width
      height: Math.max(rect.height, height * 0.9), // estimate dialog height,
      top: rect.top - 75,
      left: 0
    }
    if (width < 1000) {
      popup.width = width / 1.5;
    }
    if (width < 600) {
      popup.width = width;
    }
    popup.left = rect.left + rect.width / 2 - popup.width / 2

    if (popup.top < 0 || rect.top + popup.height * threshold > height) {
      popup.top = Math.max(0, height / 2 - popup.height / 2.5)
    }

    if (popup.left < 0 || rect.left + popup.width * threshold > width) {
      popup.left = Math.max(0, width / 2 - popup.width / 2);
    }
    setPopupState({
      top: Math.ceil(popup.top),
      left: Math.ceil(popup.left),
      width: Math.ceil(popup.width),
      height: Math.ceil(popup.height)
    });
    setSelectedPerson(person);
    setDisplayedPerson(person);
    setIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false)
  }

  // Update handleBioNavigation to support circular navigation
  const handleBioNavigation = useCallback((direction: 'left' | 'right') => {
    if (!selectedPerson || isTransitioning) return;
    
    const currentIndex = people.findIndex(p => p.name === selectedPerson.name);
    let newIndex = direction === 'right' ? currentIndex + 1 : currentIndex - 1;
    if (newIndex >= people.length) newIndex = 0;
    if (newIndex < 0) newIndex = people.length - 1;

    const newPerson = people[newIndex];
    if (!newPerson) return;

    setIsTransitioning(true);
    setImageLoaded(false);
    setSlideDirection(direction);
    setSelectedPerson(newPerson);
    setTimeout(() => setDisplayedPerson(newPerson), 50);
  }, [selectedPerson, isTransitioning, people]);

  // Update handleClick to use the hook value
  const handleClick = (direction: 'left' | 'right') => {
    if (!swiperRef.current) return;

    const swiper = swiperRef.current;
    const slidesToMove = isDesktop ? 2 : 1;
    
    if (direction === 'left' && !swiper.isBeginning) {
      swiper.slideTo(swiper.activeIndex - slidesToMove);
    } else if (direction === 'right' && !swiper.isEnd) {
      swiper.slideTo(swiper.activeIndex + slidesToMove);
    }
  };

  // Update the touch handler functions with stricter type checking
  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0];
    if (touch) {
      touchStart.current = touch.clientX;
    }
  };

  const handleTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    const touch = e.changedTouches[0];
    if (touch && typeof touchStart.current === 'number') {
      touchEnd.current = touch.clientX;
      
      const touchDiff = touchStart.current - touchEnd.current;
      const minSwipeDistance = 50;

      if (Math.abs(touchDiff) > minSwipeDistance) {
        // Swiping left (negative diff) -> show next (right)
        // Swiping right (positive diff) -> show previous (left)
        handleBioNavigation(touchDiff > 0 ? 'right' : 'left');
      }
    }
  };

  // Keyboard navigation handler wrapped in useCallback
  const handleKeyDown = useCallback((e: globalThis.KeyboardEvent) => {
    if (isOpen && isDesktop && displayedPerson && !isTransitioning) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handleBioNavigation('left');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleBioNavigation('right');
      }
    }
  }, [isOpen, isDesktop, displayedPerson, isTransitioning, handleBioNavigation]);

  // Add this useEffect hook to manage keyboard event listeners
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  return (
    <div id={id} className="py-8 mb-28">
      <div className="mx-auto lg:max-w-screen relative">
        {/* Gradient overlays - updated for better visibility in both modes */}
        {/* Left gradient - hidden at start */}
        <div className={`absolute left-0 top-0 bottom-0 w-24 z-40 pointer-events-none 
          transition-opacity duration-300 ${isAtStart ? 'opacity-0' : 'opacity-100'}`}>
          {/* Light mode gradient */}
          <div className={`absolute inset-0 bg-gradient-to-r from-secondary to-transparent 
            transition-opacity duration-300
            ${mounted && !isDarkMode ? 'opacity-100' : 'opacity-0'}`} />
          
          {/* Dark mode gradient */}
          <div className={`absolute inset-0 bg-gradient-to-r from-black to-transparent 
            transition-opacity duration-300
            ${mounted && isDarkMode ? 'opacity-100' : 'opacity-0'}`} />
        </div>

        {/* Right gradient - hidden at end */}
        <div className={`absolute right-0 top-0 bottom-0 w-24 z-40 pointer-events-none 
          transition-opacity duration-300 ${isAtEnd ? 'opacity-0' : 'opacity-100'}`}>
          {/* Light mode gradient */}
          <div className={`absolute inset-0 bg-gradient-to-l from-secondary to-transparent 
            transition-opacity duration-300
            ${mounted && !isDarkMode ? 'opacity-100' : 'opacity-0'}`} />
          
          {/* Dark mode gradient */}
          <div className={`absolute inset-0 bg-gradient-to-l from-black to-transparent 
            transition-opacity duration-300
            ${mounted && isDarkMode ? 'opacity-100' : 'opacity-0'}`} />
        </div>

        {/* Navigation indicators - improved visibility */}
        {/* Left chevron - prevent flash of wrong theme */}
        <div className={`absolute left-12 top-[40%] sm:top-1/2 -translate-y-1/2 z-20 
          transition-opacity duration-300 ${isAtStart ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} 
        >
          {/* Left chevron - smaller button */}
          <button
            onClick={() => handleClick('left')}
            aria-label="Previous team member"
            className={`flex items-center justify-center w-8 h-8 rounded-full
              ${!mounted ? 'opacity-0' : 'opacity-70'}
              ${mounted && isDarkMode 
                ? 'bg-gray-800 text-white border border-gray-700' 
                : mounted ? 'bg-white text-gray-700 border border-gray-200' : ''}
            shadow-md 
            ${isDesktop ? 'hover:opacity-100 hover:scale-110' : ''}
            ${isDesktop && isDarkMode ? 'hover:bg-gray-700' : ''}
            ${isDesktop && !isDarkMode ? 'hover:bg-gray-100' : ''}
            transition-all duration-200`}
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Right chevron - prevent flash of wrong theme */}
        <div className={`absolute right-12 top-[40%] sm:top-1/2 -translate-y-1/2 z-20 
          transition-opacity duration-300 ${isAtEnd ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} 
        >
          {/* Right chevron - smaller button */}
          <button
            onClick={() => handleClick('right')}
            aria-label="Next team member"
            className={`flex items-center justify-center w-8 h-8 rounded-full
              ${!mounted ? 'opacity-0' : 'opacity-70'}
              ${mounted && isDarkMode 
                ? 'bg-gray-800 text-white border border-gray-700' 
                : mounted ? 'bg-white text-gray-700 border border-gray-200' : ''}
            shadow-md 
            ${isDesktop ? 'hover:opacity-100 hover:scale-110' : ''}
            ${isDesktop && isDarkMode ? 'hover:bg-gray-700' : ''}
            ${isDesktop && !isDarkMode ? 'hover:bg-gray-100' : ''}
            transition-all duration-200`}
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Swiper component */}
        <Swiper
          slidesPerView={'auto'}
          slidesPerGroup={1}
          spaceBetween={SLIDE_GAP}
          mousewheel={{ forceToAxis: true }}
          grabCursor={true}
          modules={[Pagination, Mousewheel]}
          className="mySwiper !px-8"
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            // Initialize position state when swiper loads
            setIsAtStart(swiper.isBeginning);
            setIsAtEnd(swiper.isEnd);
          }}
          onSlideChange={(swiper) => {
            // Update position state whenever slides change
            setIsAtStart(swiper.isBeginning);
            setIsAtEnd(swiper.isEnd);
          }}
          observer={true}
          observeParents={true}
          resizeObserver={true}
        >
          {people.map((person) => (
            <SwiperSlide 
              key={person.name} 
              onClick={(event) => openModal(person, event)}
              className={`bg-card rounded-xl shadow transition-shadow 
                ${isDesktop ? 'hover:shadow-xl hover:bg-card-accent' : ''}
                ${styles.swiperSlide}`}
            >
              <Image width={650} height={650} alt="" src={person.imageUrl}
                     className="aspect-[3/2] w-full object-cover rounded-t-xl" />
              <h3 className="px-4 mt-6 text-3xl leading-8 tracking-tight text-gray-900 dark:text-primary pb-2 transition-colors">
                <TextProcessor>
                  {person.name}
                </TextProcessor>
              </h3>
              <p className="px-4 text-base leading-7 text-primary">{person.role}</p>
              <ul className="px-4 pb-4 mt-6 flex gap-x-6">
                {
                  person.xUrl && <li>
      <a href={person.xUrl} className="text-auxiliary hover:text-auxiliary-accent p-2 inline-block" target="_blank" rel="noopener noreferrer">
        <span className="sr-only">X</span>
        <svg fill="currentColor" viewBox="0 0 20 20" aria-hidden="true" className="h-6 w-6">
          <path d="M11.4678 8.77491L17.2961 2H15.915L10.8543 7.88256L6.81232 2H2.15039L8.26263 10.8955L2.15039 18H3.53159L8.87581 11.7878L13.1444 18H17.8063L11.4675 8.77491H11.4678ZM9.57608 10.9738L8.95678 10.0881L4.02925 3.03974H6.15068L10.1273 8.72795L10.7466 9.61374L15.9156 17.0075H13.7942L9.57608 10.9742V10.9738Z" />
        </svg>
      </a>
    </li>
                }
                {
                  person.linkedinUrl && <li>
      <a href={person.linkedinUrl} className="text-auxiliary hover:text-auxiliary-accent p-2 inline-block" target="_blank" rel="noopener noreferrer">
        <span className="sr-only">LinkedIn</span>
        <svg fill="currentColor" viewBox="0 0 20 20" aria-hidden="true" className="h-6 w-6">
          <path d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" fillRule="evenodd" />
        </svg>
      </a>
    </li>
                }
                {
                  person.githubUrl && <li>
      <a href={person.githubUrl} className="text-auxiliary hover:text-auxiliary-accent p-2 inline-block" target="_blank" rel="noopener noreferrer">
        <span className="sr-only">GitHub</span>
        <FaGithub className="h-6 w-6" aria-hidden="true" />
      </a>
    </li>
                }
              </ul>
            </SwiperSlide>
          ))}
        </Swiper>

        <Dialog as="div" className="relative z-50" onClose={closeModal} open={isOpen}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className={`fixed inset-0 ${isDarkMode ? 'bg-black/60' : 'bg-secondary/60'} backdrop-filter backdrop-grayscale overflow-y-auto`}
                 aria-hidden="true">
              <div className="flex min-h-full items-start justify-center p-4">
                <TransitionChild
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <DialogPanel
                    className="transform bg-secondary rounded-xl shadow-xl transition-all"
                    style={{
                      position: 'relative',
                      width: `${popupState.width}px`,
                      margin: '0 auto'
                    }}
                  >
                    {displayedPerson && (
                      <div 
                        className={`w-full rounded-xl shadow-xl ${isDarkMode ? 'bg-card' : 'bg-card-accent'} relative
                          will-change-transform
                          transition-all duration-250 ease-spring
                          ${isTransitioning ? `
                            opacity-95
                            ${slideDirection === 'left' ? 'translate-x-4' : ''}
                            ${slideDirection === 'right' ? '-translate-x-4' : ''}
                          ` : 'opacity-100 translate-x-0'}`}
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                      >
                        {/* Close button - adjusted to match chevron styling */}
                        <button
                          onClick={closeModal}
                          aria-label="Close team member details"
                          className={`absolute right-4 top-4 z-10 p-2 rounded-full 
                            will-change-transform
                            transition-all duration-250 ease-out
                            ${!mounted ? 'opacity-0' : 'opacity-70'} 
                            ${isDarkMode 
                              ? 'bg-gray-800 text-white border border-gray-700' 
                              : 'bg-white text-gray-800 border border-gray-200'}
                            shadow-lg 
                            ${isDesktop ? 'hover:opacity-100 hover:scale-110' : ''}
                            ${isDesktop && isDarkMode ? 'hover:bg-gray-700 hover:text-gray-100' : ''}
                            ${isDesktop && !isDarkMode ? 'hover:bg-gray-100 hover:text-black' : ''}`}
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                        
                        {/* Image - no separate translation, just scale/blur */}
                        <Image 
                          width={650} 
                          height={650} 
                          alt="" 
                          src={displayedPerson.imageUrl}
                          className={`aspect-[3/2] w-full object-cover rounded-t-xl
                            will-change-transform
                            transition-all duration-250 ease-out
                            ${isTransitioning ? 'scale-[0.99] blur-[2px]' : 'scale-100 blur-0'}`}
                          onLoadingComplete={() => {
                            setImageLoaded(true);
                            setIsTransitioning(false);
                            setSlideDirection(null);
                          }}
                        />
                        
                        {/* Content - no separate translation, just opacity */}
                        <div 
                          className={`px-8 py-6
                            will-change-opacity
                            transition-opacity duration-250 ease-out
                            ${(!imageLoaded || isTransitioning) ? 'opacity-0' : 'opacity-100'}`}
                        >
                          <h3 className="text-3xl leading-8 tracking-tight text-primary pb-2">
                            <TextProcessor>{displayedPerson.name}</TextProcessor>
                          </h3>
                          <p className="text-base leading-7 text-primary">{displayedPerson.role}</p>
                          <p className="mt-6 text-lg text-auxiliary dark:text-gray-300 transition-colors">
                            {displayedPerson.bio}
                          </p>
                          <ul className="mt-6 flex gap-x-6">
                            {
                              displayedPerson.xUrl && <li>
      <a href={displayedPerson.xUrl} className="text-auxiliary hover:text-auxiliary-accent p-2 inline-block" target="_blank" rel="noopener noreferrer">
        <span className="sr-only">X</span>
        <svg fill="currentColor" viewBox="0 0 20 20" aria-hidden="true" className="h-6 w-6">
          <path d="M11.4678 8.77491L17.2961 2H15.915L10.8543 7.88256L6.81232 2H2.15039L8.26263 10.8955L2.15039 18H3.53159L8.87581 11.7878L13.1444 18H17.8063L11.4675 8.77491H11.4678ZM9.57608 10.9738L8.95678 10.0881L4.02925 3.03974H6.15068L10.1273 8.72795L10.7466 9.61374L15.9156 17.0075H13.7942L9.57608 10.9742V10.9738Z" />
        </svg>
      </a>
    </li>
                            }
                            {
                              displayedPerson.linkedinUrl && <li>
      <a href={displayedPerson.linkedinUrl} className="text-auxiliary hover:text-auxiliary-accent p-2 inline-block" target="_blank" rel="noopener noreferrer">
        <span className="sr-only">LinkedIn</span>
        <svg fill="currentColor" viewBox="0 0 20 20" aria-hidden="true" className="h-6 w-6">
          <path d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" fillRule="evenodd" />
        </svg>
      </a>
    </li>
                            }
                            {
                              displayedPerson.githubUrl && <li>
      <a href={displayedPerson.githubUrl} className="text-auxiliary hover:text-auxiliary-accent p-2 inline-block" target="_blank" rel="noopener noreferrer">
        <span className="sr-only">GitHub</span>
        <FaGithub className="h-6 w-6" aria-hidden="true" />
      </a>
    </li>
                            }
                          </ul>
                        </div>
                      </div>
                    )}
                  </DialogPanel>
                </TransitionChild>
              </div>
            </div>
          </TransitionChild>
        </Dialog>
      </div>
    </div>
  )
}

export default Team;
